import {useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Avatar, AvatarFallback, AvatarImage} from '@/components/ui/avatar.tsx';
import {Button} from '@/components/ui/button.tsx';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';
import {User} from "@/types/User.ts";
import {UserRole} from "@/types/UserRole.ts";
import {getImgSrc} from "@/utils/file-upload.ts";
import {useTranslation} from "react-i18next";
import {getUsers} from "@/services/userService.ts";
import {impersonateUser, stopImpersonation} from "@/services/impersonationService.ts";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {useToast} from "@/components/ui/use-toast.ts";

interface ProfileDropdownProps {
    user: User
}

function ProfileDropdown({user}: ProfileDropdownProps) {
    const {t} = useTranslation();
    const isAdmin = user.role === UserRole.ADMIN;
    const isImpersonating = Boolean(user.impersonator);
    const [impersonateOpen, setImpersonateOpen] = useState(false);
    const [roleFilter, setRoleFilter] = useState<UserRole>(UserRole.MENTOR);
    const [selectedUserId, setSelectedUserId] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const {toast} = useToast();

    const {data: users} = useQuery({
        queryKey: ["impersonateUsers"],
        queryFn: () => getUsers(),
        enabled: isAdmin && impersonateOpen,
        initialData: [],
    });

    const filteredUsers = useMemo(() => {
        return (users ?? []).filter((u) => u.role === roleFilter);
    }, [users, roleFilter]);

    const handleImpersonate = async () => {
        if (!selectedUserId) return;
        try {
            setIsSubmitting(true);
            await impersonateUser(parseInt(selectedUserId, 10));
            window.location.href = "/dashboard";
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: t("common.errorTitle"),
                description: error?.message ?? t("common.errorDesc"),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStopImpersonation = async () => {
        try {
            setIsSubmitting(true);
            await stopImpersonation();
            window.location.href = "/dashboard";
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: t("common.errorTitle"),
                description: error?.message ?? t("common.errorDesc"),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-10 w-10">
                    <Avatar>
                        <AvatarImage src={getImgSrc(user.profilePictureSrc)}/>
                        <AvatarFallback>{user.fullName?.charAt(0)}</AvatarFallback>
                    </Avatar>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                        <p className="font-medium leading-none">{user.fullName}</p>
                        <p className="text-sm leading-none text-muted-foreground">{user.email}</p>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator/>
                <DropdownMenuGroup>
                    <DropdownMenuItem>
                        <a href={user.role === UserRole.MENTOR ?
                            `/mentors/${user.mentor?.id}` : user.role === UserRole.MENTEE ?
                                `/mentees/${user.mentee?.id}` : `/users/${user.id}`}>
                            {t("navbar.myProfile")}
                        </a>
                    </DropdownMenuItem>
                    {user.role === UserRole.ADMIN &&
                        <>
                            <DropdownMenuItem>
                                <a href="/settings">{t("common.settings")}</a>
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                                <a href="/audit-log">{t("navbar.auditLog")}</a>
                            </DropdownMenuItem>
                        </>
                    }
                    <DropdownMenuItem>
                        <a href="/auth/logout">{t("common.signOut")}</a>
                    </DropdownMenuItem>
                </DropdownMenuGroup>
                {isAdmin && (
                    <>
                        <DropdownMenuSeparator/>
                        <DropdownMenuGroup>
                            {isImpersonating ? (
                                <DropdownMenuItem onClick={handleStopImpersonation} disabled={isSubmitting}>
                                    {t("navbar.stopImpersonation")}
                                </DropdownMenuItem>
                            ) : (
                                <Dialog open={impersonateOpen} onOpenChange={setImpersonateOpen}>
                                    <DialogTrigger asChild>
                                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                            {t("navbar.impersonate")}
                                        </DropdownMenuItem>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>{t("navbar.impersonate")}</DialogTitle>
                                            <DialogDescription>{t("navbar.impersonateDesc")}</DialogDescription>
                                        </DialogHeader>
                                        <p className="text-xs text-muted-foreground">
                                            {t("navbar.impersonationReadOnly")}
                                        </p>
                                        <div className="space-y-4">
                                            <div className="space-y-1">
                                                <label className="text-xs font-medium text-muted-foreground">
                                                    {t("navbar.impersonateRole")}
                                                </label>
                                                <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v as UserRole)}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t("common.role")} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value={UserRole.MENTOR}>{t("common.mentor")}</SelectItem>
                                                        <SelectItem value={UserRole.MENTEE}>{t("common.mentee")}</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-xs font-medium text-muted-foreground">
                                                    {t("navbar.impersonateUser")}
                                                </label>
                                                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t("navbar.impersonateSelectUser")} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {filteredUsers.map((u) => (
                                                            <SelectItem key={u.id} value={String(u.id)}>
                                                                {u.fullName} ({u.email})
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <DialogFooter>
                                            <Button variant="outline" onClick={() => setImpersonateOpen(false)}>
                                                {t("common.cancel")}
                                            </Button>
                                            <Button onClick={handleImpersonate} disabled={!selectedUserId || isSubmitting}>
                                                {t("navbar.impersonateConfirm")}
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            )}
                        </DropdownMenuGroup>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default ProfileDropdown;
